import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Phase 18B — shared @mention popover.
 *
 * Wraps any controlled <input>/<textarea>. Caller passes:
 *  - `inputRef`: ref to the input/textarea element.
 *  - `value`: the current text.
 *  - `onChange(newValue, newCaret)`: called when the user inserts a handle.
 *  - `token`, `API`: for the autocomplete API call.
 *
 * Trigger rules (matches Twitter/IG):
 *  - User typed `@` followed by at least one valid handle char and no
 *    whitespace before the cursor.
 *  - Word break (space, newline, end of string, or another `@`) ends the
 *    candidate.
 *  - Backspacing past the `@` dismisses.
 *  - Esc dismisses.
 *  - Up/Down arrow + Enter or Tab insert; Esc closes.
 */
const Avatar = ({ src, name }) =>
  src ? (
    <img src={src} alt={name || ''} className="w-6 h-6 rounded-full object-cover" />
  ) : (
    <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 text-white text-[10px] font-semibold flex items-center justify-center">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );

// Find the active @-prefix to the LEFT of the caret. Returns
// { start, end, prefix } or null.
function findActiveMention(text, caret) {
  if (!text || caret == null || caret <= 0) return null;
  // Walk back from the caret until whitespace, '@' boundary, or start.
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      // Must be at start, or preceded by a non-handle char.
      if (i === 0 || /[^A-Za-z0-9_.\-]/.test(text[i - 1])) {
        const prefix = text.slice(i + 1, caret);
        // The candidate must be valid handle chars only — abort otherwise.
        if (!/^[A-Za-z0-9_.\-]*$/.test(prefix)) return null;
        return { start: i, end: caret, prefix };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    if (!/[A-Za-z0-9_.\-]/.test(ch)) return null;
    i--;
  }
  return null;
}

const MentionAutocomplete = ({
  inputRef,
  value,
  onChange,
  token,
  API,
  className = '',
  testIdPrefix = 'mention',
  isRTL = false,
}) => {
  const [active, setActive] = useState(null);   // { start, end, prefix } | null
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const abortRef = useRef(null);
  // Tracks the (start, prefix) we last dismissed via Esc — so a `keyup` or
  // text-change with the SAME prefix won't immediately re-open.
  const dismissedRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const recomputeActive = () => {
    const el = inputRef?.current;
    if (!el) { setActive(null); return; }
    const caret = el.selectionStart ?? value.length;
    const a = findActiveMention(value, caret);
    if (a && dismissedRef.current
        && dismissedRef.current.start === a.start
        && dismissedRef.current.prefix === a.prefix) {
      // User explicitly dismissed this exact trigger. Wait for them to type
      // or move out before re-opening.
      setActive(null);
      setResults([]);
      return;
    }
    // Any movement to a different active position (or no active) clears the
    // dismiss memo so future triggers can open normally.
    if (!a || dismissedRef.current?.start !== a?.start) {
      dismissedRef.current = null;
    }
    setActive(a);
    if (!a) setResults([]);
  };

  useEffect(() => {
    recomputeActive();
  }, [value]);

  // Caret-moved without text change → mouse click in middle of word.
  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    const handler = () => recomputeActive();
    el.addEventListener('keyup', handler);
    el.addEventListener('click', handler);
    return () => {
      el.removeEventListener('keyup', handler);
      el.removeEventListener('click', handler);
    };
  }, [inputRef?.current, value]);

  // Debounced autocomplete fetch.
  useEffect(() => {
    if (!active) { setResults([]); return; }
    const prefix = active.prefix.trim();
    if (!prefix) { setResults([]); return; }
    abortRef.current?.abort?.();
    const ctl = new AbortController();
    abortRef.current = ctl;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/mentions/autocomplete`, {
          params: { prefix, limit: 6 },
          headers,
          signal: ctl.signal,
        });
        setResults(res.data?.results || []);
        setHighlight(0);
      } catch (err) {
        if (axios.isCancel?.(err)) return;
        setResults([]);
      }
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [active?.prefix, API, token]);

  const insert = (user) => {
    if (!active) return;
    const handle = user.nickname_normalized || user.nickname;
    if (!handle) return;
    const before = value.slice(0, active.start);
    const after = value.slice(active.end);
    // Insert "@handle " (with trailing space for fluency) and place caret after it.
    const insertion = `@${handle} `;
    const next = before + insertion + after;
    const newCaret = (before + insertion).length;
    onChange(next, newCaret);
    setActive(null);
    setResults([]);
    // Restore caret position after React updates the DOM.
    requestAnimationFrame(() => {
      const el = inputRef?.current;
      if (el && el.setSelectionRange) {
        try { el.setSelectionRange(newCaret, newCaret); el.focus(); } catch (e) { /* noop */ }
      }
    });
  };

  // Keyboard navigation while popover is open. We attach in capture phase so
  // we win over any parent modal/dialog that might swallow Escape, and we
  // expose a programmatic API via a ref-stored handler that input wrappers
  // can call from their own React onKeyDown if needed.
  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    const onKey = (e) => {
      if (!active || results.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (h + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => (h - 1 + results.length) % results.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        insert(results[highlight]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Memoise this exact trigger so the keyup/text-unchanged path can't
        // immediately re-open the popover.
        dismissedRef.current = { start: active.start, prefix: active.prefix };
        setActive(null);
        setResults([]);
      }
    };
    // capture:true so a parent dialog/modal can't swallow Escape first.
    el.addEventListener('keydown', onKey, true);
    return () => el.removeEventListener('keydown', onKey, true);
  }, [active, results, highlight]);

  if (!active || results.length === 0) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-popover`}
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`absolute z-50 left-0 right-0 -top-2 -translate-y-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden ${className}`}
    >
      <ul role="listbox">
        {results.map((u, i) => (
          <li
            key={u.id}
            data-testid={`${testIdPrefix}-item-${u.nickname_normalized}`}
            role="option"
            aria-selected={i === highlight}
            onMouseDown={(e) => { e.preventDefault(); insert(u); }}
            onMouseEnter={() => setHighlight(i)}
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${
              i === highlight ? 'bg-purple-50' : 'hover:bg-gray-50'
            }`}
          >
            <Avatar src={u.profile_picture_url} name={u.name} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{u.name || u.nickname}</div>
              <div className="text-xs text-gray-500 truncate">@{u.nickname || u.nickname_normalized}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MentionAutocomplete;
